const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://192.168.1.44:3001/api';
//NOTE envvar must be prefixed w REACT_APP_ to be avail. via process.env in client-side code

async function apiCall(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;
  
  // Get auth token if available
  const token = localStorage.getItem('authToken');
  
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, config);
    const data = await response.json();

    if (!response.ok) {
      // Handle 401 Unauthorized - redirect to login
      if (response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      throw new Error(data.message || 'API request failed');
    }

    return data;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
}

// ============================================
// AUTH API
// ============================================
export const authAPI = {
  login: (email, password) => 
    apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  
  logout: () => 
    apiCall('/auth/logout', {
      method: 'POST',
    }),
  
  validateToken: (token) => 
    apiCall('/auth/validate', {
      headers: { Authorization: `Bearer ${token}` },
    }),
};

// ============================================
// DASHBOARD API
// ============================================
export const dashboardAPI = {
  getOverview: () => apiCall('/dashboard/overview'),
  getGlobalDistribution: () => apiCall('/dashboard/global-distribution'),
  getMonthlyInsects: (years = '2023,2024,2025') => 
    apiCall(`/dashboard/monthly-insects?years=${years}`),
  getYearlySpecies: (years = '2023,2024,2025') => 
    apiCall(`/dashboard/yearly-species?years=${years}`),
};

// ============================================
// SUMMARY API
// ============================================
export const summaryAPI = {
  getKPIs: (dateRange) => {
    const params = new URLSearchParams();
    if (dateRange?.from) params.append('rangeFrom', dateRange.from);
    if (dateRange?.to) params.append('rangeTo', dateRange.to);
    const queryString = params.toString();
    return apiCall(`/summary/kpis${queryString ? `?${queryString}` : ''}`);
  },

  getAllDevices: (dateRange) => {
    const params = new URLSearchParams();
    if (dateRange?.from) params.append('rangeFrom', dateRange.from);
    if (dateRange?.to) params.append('rangeTo', dateRange.to);
    const queryString = params.toString();
    return apiCall(`/summary/devices${queryString ? `?${queryString}` : ''}`);
  },

  getFilterOptions: () => apiCall('/summary/filter-options'),
};

// ============================================
// ANALYTICS API
// ============================================
export const analyticsAPI = {
  getFilterOptions: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.global) params.append('global', filters.global);
    if (filters.region) params.append('region', filters.region);
    if (filters.client) params.append('client', filters.client);
    if (filters.address) params.append('address', filters.address);
    if (filters.location) params.append('location', filters.location);
    
    const queryString = params.toString();
    return apiCall(`/analytics/filter-options${queryString ? `?${queryString}` : ''}`);
  },
  
  getMonthlyTotalInsects: (filters) => {
    const params = new URLSearchParams();
    if (filters.global) params.append('global', filters.global);
    if (filters.region) params.append('region', filters.region);
    if (filters.client) params.append('client', filters.client);
    if (filters.address) params.append('address', filters.address);
    if (filters.location) params.append('location', filters.location);
    if (filters.device) params.append('device', filters.device);
    if (filters.species) params.append('species', filters.species);
    if (filters.range?.from) params.append('rangeFrom', filters.range.from);
    if (filters.range?.to) params.append('rangeTo', filters.range.to);
    
    return apiCall(`/analytics/monthly-total-insects?${params.toString()}`);
  },

  getMonthlyCaptureTrend: (filters) => {
    const params = new URLSearchParams();
    if (filters.global) params.append('global', filters.global);
    if (filters.region) params.append('region', filters.region);
    if (filters.client) params.append('client', filters.client);
    if (filters.address) params.append('address', filters.address);
    if (filters.location) params.append('location', filters.location);
    if (filters.device) params.append('device', filters.device);
    if (filters.threshold) params.append('threshold', filters.threshold);
    if (filters.range?.from) params.append('rangeFrom', filters.range.from);
    if (filters.range?.to) params.append('rangeTo', filters.range.to);
    
    return apiCall(`/analytics/monthly-capture-trend?${params.toString()}`);
  },

  getSpeciesIdentifiedByLocation: (filters) => {
    const params = new URLSearchParams();
    if (filters.global) params.append('global', filters.global);
    if (filters.region) params.append('region', filters.region);
    if (filters.client) params.append('client', filters.client);
    if (filters.address) params.append('address', filters.address);
    if (filters.location) params.append('location', filters.location);
    if (filters.device) params.append('device', filters.device);
    if (filters.species) params.append('species', filters.species);
    if (filters.range?.from) params.append('rangeFrom', filters.range.from);
    
    return apiCall(`/analytics/species-identified?${params.toString()}`);
  },

  getSpeciesTrendByLocation: (filters) => {
    const params = new URLSearchParams();
    if (filters.global) params.append('global', filters.global);
    if (filters.region) params.append('region', filters.region);
    if (filters.client) params.append('client', filters.client);
    if (filters.address) params.append('address', filters.address);
    if (filters.location) params.append('location', filters.location);
    if (filters.device) params.append('device', filters.device);
    if (filters.species) params.append('species', filters.species);
    if (filters.range?.from) params.append('rangeFrom', filters.range.from);
    if (filters.range?.to) params.append('rangeTo', filters.range.to);
    
    return apiCall(`/analytics/species-trend?${params.toString()}`);
  }
};
